import { Component } from "@theme/component";

class CountdownTimer extends Component {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.init();
  }

  init() {
    this.MS_IN_SEC = 1000;
    this.MS_IN_MIN = 60 * this.MS_IN_SEC;
    this.MS_IN_HRS = 60 * this.MS_IN_MIN;
    this.MS_IN_DAY = 24 * this.MS_IN_HRS;

    this.units = ["day", "hour", "minute", "second"];
    this.type = this.dataset.type;
    this.duration = this.dataset.duration;
    this.loop = false;
    this.shouldAddLeadingZero = true;

    // Optional scheduled start. Without data-start-date the timer behaves exactly as
    // before, so existing usages are unaffected.
    const startDate = this.dataset.startDate ? new Date(this.dataset.startDate).getTime() : NaN;
    this.scheduledStart = Number.isNaN(startDate) ? null : startDate;

    // Optional: drop the days unit while it reads zero, so a short countdown in a
    // slim bar shows hours:minutes:seconds instead of a leading "00 :".
    this.hideEmptyDays = this.hasAttribute("data-hide-empty-days");

    this.initCountdown();
  }

  disconnectedCallback() {
    clearInterval(this.intervalId);
    clearInterval(this.startWatcherId);
    super.disconnectedCallback?.();
  }

  initCountdown() {
    switch (this.type) {
      case "fixed_time":
        const countdownData = this.dataset.dueDate;
        if (countdownData) {
          this.targetTime = new Date(countdownData).getTime();
        }
        break;
      default: // Evergreen.
        this.targetTime = this.getEvergreenDueDate().getTime();
        break;
    }

    if (!this.targetTime) {
      this.terminateCountdown();
      return;
    }

    this.startTime = Date.now();

    // Before the scheduled start there is nothing to count down to yet, so stay
    // hidden and poll until the window opens.
    if (this.scheduledStart && this.startTime < this.scheduledStart) {
      this.waitForScheduledStart();
      return;
    }

    if (this.targetTime > this.startTime) {
      this.beginCountdown();
    } else {
      this.terminateCountdown();
    }
  }

  waitForScheduledStart() {
    this.hide();

    clearInterval(this.startWatcherId);
    this.startWatcherId = setInterval(() => {
      if (Date.now() < this.scheduledStart) return;

      clearInterval(this.startWatcherId);
      this.show();
      this.initCountdown();
    }, 1000);
  }

  hide() {
    if (Shopify.designMode) return;
    this.classList.remove("flex");
    this.classList.add("hidden");
  }

  show() {
    if (Shopify.designMode) return;
    this.classList.remove("hidden");
    this.classList.add("flex");
  }

  beginCountdown() {
    this.intervalId = setInterval(() => {
      const remainingTime = this.targetTime - Date.now();
      if (remainingTime <= 0) {
        this.terminateCountdown();
      } else {
        this.updateDisplay(remainingTime);
      }
    }, 1000);
  }

  updateDisplay(remainingTime) {
    const timeComponents = this.computeTimeComponents(remainingTime);
    this.units.forEach((unit) => {
      if (this.refs[unit]) {
        this.refs[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
      }
    });

    if (this.hideEmptyDays) {
      const dayBlock = this.refs.day?.closest(".countdown-timer__block");
      // Toggled with an inline style, not the hidden attribute: the block carries the
      // inline-flex utility class, and a class selector beats the user agent's
      // [hidden] { display: none }, so the attribute alone does nothing here.
      if (dayBlock) dayBlock.style.display = timeComponents.day === 0 ? "none" : "";
    }
  }

  computeTimeComponents(ms) {
    return {
      day: Math.floor(ms / this.MS_IN_DAY),
      hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
      minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
      second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
    };
  }

  formatTimeComponent(value) {
    return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
  }

  terminateCountdown() {
    clearInterval(this.intervalId);
    if (this.loop) {
      this.beginCountdown();
    } else {
      this.hide();
    }
  }

  resetCountdown() {
    clearInterval(this.intervalId);
    this.units.forEach((unit) => {
      if (this.refs[unit]) {
        this.refs[unit].textContent = "00";
      }
    });
  }

  getEvergreenDueDate() {
    let intervalHrs;
    const now = new Date();
    const dueDate = new Date(now);

    switch (this.duration) {
      case "every_month":
        dueDate.setMonth(dueDate.getMonth() + 1); // Next month.
        dueDate.setDate(1); // Start from the 1st day of the next month.
        dueDate.setHours(0, 0, 0, 0);
        return dueDate;
      case "every_week":
        const dayOfWeek = now.getDay();
        const daysUntilNextWeek = 7 - dayOfWeek;
        dueDate.setDate(now.getDate() + daysUntilNextWeek); // Move to next Sunday.
        dueDate.setHours(0, 0, 0, 0); // Set the time to 00:00:00.
        return dueDate;
      case "every_2_hrs":
        intervalHrs = 2;
        break;
      case "every_6_hrs":
        intervalHrs = 6;
        break;
      case "every_12_hrs":
        intervalHrs = 12;
        break;
      case "every_day":
        intervalHrs = 24;
        break;
    }

    const currentHour = now.getHours();

    // Possible start points based on the interval.
    const startPoints = [];
    for (let i = 0; i < 24; i += intervalHrs) {
      startPoints.push(i);
    }

    // Find the next start point.
    for (let i = 0; i < startPoints.length; i++) {
      if (
        currentHour < startPoints[i] ||
        (currentHour === startPoints[i] && now.getMinutes() === 0 && now.getSeconds() === 0)
      ) {
        dueDate.setHours(startPoints[i], 0, 0, 0);
        return dueDate;
      }
    }

    // If no future point today, roll over to the first point tomorrow.
    dueDate.setDate(dueDate.getDate() + 1);
    dueDate.setHours(startPoints[0], 0, 0, 0);
    return dueDate;
  }
}

if (!customElements.get("countdown-timer")) {
  customElements.define("countdown-timer", CountdownTimer);
}
